import { useAuth0 } from '@auth0/auth0-react';

export const ExpenseMainCategory = Object.freeze({
  Add: 0,
  Entertainment: 1,
  Home: 2,
  Other: 3,
  Obos: 4,
  Weekly: 5,
});

export const CashFlowType = Object.freeze({
  In: 0,
  Out: 1,
});

const useCalculatorService = () => {
  const { getAccessTokenSilently } = useAuth0();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  // Helper: try to get token, but allow anonymous calls (same style as your blog service)
  const tryGetAccessToken = async () => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: { scope: 'read:current_user' },
      });
    } catch (error) {
      console.log('User is not authenticated:', error);
      return undefined;
    }
  };

  // Helper: build headers (only add Authorization if we actually have a token)
  const buildHeaders = (accessToken, extra = {}) => {
    const headers = { ...extra };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return headers;
  };

  // ########################
  // AccountModel endpoints
  // ########################

  const addAccountModel = async (model) => {
    const accessToken = await tryGetAccessToken();
    const response = await fetch(`${baseUrl}/api/Calculator/accountmodel`, {
      method: 'POST',
      headers: buildHeaders(accessToken, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(model),
    });
    return response;
  };

  // GET api/Calculator/accountmodel?from=...
  const getAccountModels = async (from) => {
    const accessToken = await tryGetAccessToken();
    const params = new URLSearchParams({ from: from.toISOString() });

    const response = await fetch(`${baseUrl}/api/Calculator/accountmodel?${params.toString()}`, {
      headers: buildHeaders(accessToken),
    });

    return await response.json();
  };

  // POST api/Calculator/refreshAccountModels?from=...&now=...&dateTo=...
  const refreshAccountModels = async ({ from, now, dateTo }) => {
    const accessToken = await tryGetAccessToken();
    const params = new URLSearchParams({
      from: new Date(from).toISOString(),
      now: new Date(now).toISOString(),
      dateTo: new Date(dateTo).toISOString(),
    });

    const response = await fetch(`${baseUrl}/api/Calculator/refreshAccountModels?${params.toString()}`, {
      method: 'POST',
      headers: buildHeaders(accessToken),
    });

    return response;
  };

  // PATCH api/Calculator/accountModel?id=...&amount=...
  const updateAccountModel = async ({ id, amount }) => {
    const accessToken = await tryGetAccessToken();
    const params = new URLSearchParams({
      id: String(id),
      amount: String(amount),
    });

    const response = await fetch(`${baseUrl}/api/Calculator/accountModel?${params.toString()}`, {
      method: 'PATCH',
      headers: buildHeaders(accessToken),
    });

    return response;
  };

  // ########################
  // Limit endpoints
  // ########################

  const addLimit = async (model) => {
    const accessToken = await tryGetAccessToken();
    const response = await fetch(`${baseUrl}/api/Calculator/limit`, {
      method: 'POST',
      headers: buildHeaders(accessToken, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(model),
    });
    return response;
  };

  // GET api/Calculator/limit?fromDate=...
  const getLimits = async ({ fromDate }) => {
    const accessToken = await tryGetAccessToken();
    const params = new URLSearchParams({
      fromDate: new Date(fromDate).toISOString()
    });

    const response = await fetch(`${baseUrl}/api/Calculator/limits?${params.toString()}`, {
      headers: buildHeaders(accessToken),
    });

    return await response.json();
  };

  // PATCH api/Calculator/limit?id=...&amount=...
  const updateLimit = async ({ id, amount }) => {
    const accessToken = await tryGetAccessToken();
    const params = new URLSearchParams({
      id: String(id),
      amount: String(amount),
    });

    const response = await fetch(`${baseUrl}/api/Calculator/limit?${params.toString()}`, {
      method: 'PATCH',
      headers: buildHeaders(accessToken),
    });

    return response;
  };

  // POST api/Calculator/refreshLimits?from=...&now=...&category=...&dateTo=...
  const refreshLimits = async ({ from, now, category, dateTo }) => {
    const accessToken = await tryGetAccessToken();
    const params = new URLSearchParams({
      from: new Date(from).toISOString(),
      now: new Date(now).toISOString(),
      category: String(category),
      dateTo: new Date(dateTo).toISOString(),
    });

    const response = await fetch(`${baseUrl}/api/Calculator/refreshLimits?${params.toString()}`, {
      method: 'POST',
      headers: buildHeaders(accessToken),
    });

    return response;
  };

  // ########################
  // Expense endpoints
  // ########################

  // POST api/Calculator/expense?limitId=123 (limitId optional)
  const addExpense = async ({ expense, limitId = null }) => {
    const accessToken = await tryGetAccessToken();
    const params = new URLSearchParams();
    if (limitId !== null && limitId !== undefined) params.set('limitId', String(limitId));

    const response = await fetch(`${baseUrl}/api/Calculator/expense?${params.toString()}`, {
      method: 'POST',
      headers: buildHeaders(accessToken, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(expense),
    });

    return response;
  };

  // GET api/Calculator/expenses?from=...
  const getExpenses = async (from) => {
    const accessToken = await tryGetAccessToken();
    const params = new URLSearchParams({ from: new Date(from).toISOString() });

    const response = await fetch(`${baseUrl}/api/Calculator/expenses?${params.toString()}`, {
      headers: buildHeaders(accessToken),
    });

    // backend returns IActionResult -> Ok(expenses)
    return await response.json();
  };

  // GET api/Calculator/limit/expense?limitId=...
  const getExpensesByLimit = async (limitId) => {
    const accessToken = await tryGetAccessToken();
    const params = new URLSearchParams({ limitId: String(limitId) });

    const response = await fetch(`${baseUrl}/api/Calculator/limit/expense?${params.toString()}`, {
      headers: buildHeaders(accessToken),
    });

    return await response.json();
  };

  const updateExpense = async ({ id, expense }) => {
    const accessToken = await tryGetAccessToken();
    const params = new URLSearchParams({
      id: String(id),
    });

    const response = await fetch(`${baseUrl}/api/Calculator/expense?${params.toString()}`, {
      method: 'PATCH',
      headers: buildHeaders(accessToken, { 'Content-Type': 'application/json' }),
      body: JSON.stringify(expense),
    });

    return response;
  };

  const deleteExpense = async (id) => {
    const accessToken = await tryGetAccessToken();
    const params = new URLSearchParams({ id: String(id) });

    return await fetch(`${baseUrl}/api/Calculator/expense?${params.toString()}`, {
      method: 'DELETE',
      headers: buildHeaders(accessToken),
    });
  };

  return {
    // AccountModel
    addAccountModel,
    getAccountModels,
    refreshAccountModels,
    updateAccountModel,

    // Limits
    addLimit,
    getLimits,
    updateLimit,
    refreshLimits,

    // Expenses
    addExpense,
    getExpenses,
    getExpensesByLimit,
    updateExpense,
    deleteExpense
  };
};

export default useCalculatorService;